import { useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getPlatformColor } from "./constants";

export interface PostDot {
  id: string;
  platform: string;
  status: string;
  scheduled_at: string;
  type?: "blog" | "social";
}

interface Props {
  year: number;
  month: number;  // 0-indexed
  posts: PostDot[];
  selectedDate: string | null;  // 'YYYY-MM-DD'
  onSelectDate: (date: string | null) => void;
  onPrevMonth: () => void;
  onNextMonth: () => void;
}

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];
const DAY_NAMES = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

export function PostCalendarGrid({
  year, month, posts, selectedDate, onSelectDate, onPrevMonth, onNextMonth
}: Props) {
  // Build a map: 'YYYY-MM-DD' → PostDot[]
  const postsByDate = useMemo(() => {
    const map: Record<string, PostDot[]> = {};
    for (const p of posts) {
      if (!p.scheduled_at) continue;
      const d = new Date(p.scheduled_at);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      if (!map[key]) map[key] = [];
      map[key].push(p);
    }
    return map;
  }, [posts]);

  // Generate calendar cells
  const cells = useMemo(() => {
    const firstDay = new Date(year, month, 1).getDay(); // 0=Sun
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const result: Array<{ date: number | null; key: string | null }> = [];

    // Leading empty cells
    for (let i = 0; i < firstDay; i++) {
      result.push({ date: null, key: null });
    }
    // Day cells
    for (let d = 1; d <= daysInMonth; d++) {
      const key = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      result.push({ date: d, key });
    }
    // Trailing cells to fill last row
    const remainder = result.length % 7;
    if (remainder !== 0) {
      for (let i = 0; i < 7 - remainder; i++) {
        result.push({ date: null, key: null });
      }
    }
    return result;
  }, [year, month]);

  const todayKey = (() => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
  })();

  const handleDayClick = (key: string) => {
    if (selectedDate === key) {
      onSelectDate(null); // Deselect
    } else {
      onSelectDate(key);
    }
  };

  return (
    <div className="rounded-lg border bg-card">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <Button variant="ghost" size="sm" onClick={onPrevMonth}>
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <h3 className="font-semibold text-base">
          {MONTH_NAMES[month]} {year}
        </h3>
        <Button variant="ghost" size="sm" onClick={onNextMonth}>
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Day-of-week headers */}
      <div className="grid grid-cols-7 text-xs text-muted-foreground text-center border-b">
        {DAY_NAMES.map(d => (
          <div key={d} className="py-2 font-medium">{d}</div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7">
        {cells.map((cell, idx) => {
          if (!cell.key || cell.date === null) {
            return <div key={idx} className="min-h-[72px] border-b border-r last:border-r-0 bg-muted/20" />;
          }

          const dayPosts = postsByDate[cell.key] ?? [];
          const isToday = cell.key === todayKey;
          const isSelected = cell.key === selectedDate;

          // Show up to 5 platform dots, then "+N"
          const visiblePosts = dayPosts.slice(0, 5);
          const overflow = dayPosts.length - visiblePosts.length;

          return (
            <button
              key={cell.key}
              onClick={() => handleDayClick(cell.key!)}
              className={`min-h-[72px] p-1.5 border-b border-r last:border-r-0 text-left transition-colors hover:bg-accent/50 ${
                isSelected ? 'bg-primary/10 ring-1 ring-inset ring-primary' : ''
              } ${idx % 7 === 6 ? 'border-r-0' : ''}`}
            >
              {/* Day number */}
              <div className={`text-xs font-medium mb-1 w-5 h-5 flex items-center justify-center rounded-full ${
                isToday ? 'bg-primary text-primary-foreground' : 'text-foreground'
              }`}>
                {cell.date}
              </div>

              {/* Platform dots */}
              {dayPosts.length > 0 && (
                <div className="flex flex-wrap gap-0.5">
                  {visiblePosts.map((p, i) => {
                    const color = p.type === 'blog' ? '#C5A55A' : getPlatformColor(p.platform);
                    const displayColor = color === '#000000' ? '#374151' : color;
                    return (
                      <span
                        key={i}
                        className="inline-block w-2 h-2 rounded-full"
                        style={{ backgroundColor: displayColor }}
                        title={`${p.type === 'blog' ? 'Blog' : p.platform} — ${p.status}`}
                      />
                    );
                  })}
                  {overflow > 0 && (
                    <span className="text-[9px] text-muted-foreground leading-2">+{overflow}</span>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="px-4 py-2 border-t flex items-center gap-4 text-xs text-muted-foreground">
        <span>
          {Object.values(postsByDate).flat().length} post{Object.values(postsByDate).flat().length !== 1 ? 's' : ''} this month
        </span>
        {selectedDate && (
          <span className="text-primary">
            Showing posts for {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            <button
              onClick={() => onSelectDate(null)}
              className="ml-1 underline"
            >
              (clear)
            </button>
          </span>
        )}
      </div>
    </div>
  );
}
